// packages/web/src/components/AdminSidebar.tsx
import React from 'react'
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Layers,
  Sliders,
  Store,
  LogOut,
  X,
  Menu,
  MoreHorizontal,
  Tag,
  Sparkles,
} from 'lucide-react'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useAuth } from '../context/AuthContext'
import { useSiteConfig } from '../context/SiteConfigContext'

export type AdminSubTab =
  | 'overview'
  | 'products'
  | 'categories'
  | 'combos'
  | 'orders'
  | 'stock'
  | 'settings'

const SUB_TABS: AdminSubTab[] = ['overview', 'products', 'categories', 'orders', 'stock', 'settings']

/** Primary tabs shown in the mobile bottom bar */
const MOBILE_BOTTOM_TABS: AdminSubTab[] = ['overview', 'orders', 'products']

export function tabFromPath(pathname: string): AdminSubTab {
  const base = '/admin/'
  if (!pathname.startsWith(base)) return 'overview'
  const segment = pathname.slice(base.length).split('/')[0] ?? ''
  return (SUB_TABS as string[]).includes(segment) ? (segment as AdminSubTab) : 'overview'
}

export const ADMIN_TABS: {
  key: AdminSubTab
  label: string
  shortLabel: string
  icon: React.ComponentType<{ className?: string }>
}[] = [
  { key: 'overview', label: 'Overview', shortLabel: 'Home', icon: LayoutDashboard },
  { key: 'products', label: 'Products & Variants', shortLabel: 'Products', icon: Package },
  { key: 'categories', label: 'Categories', shortLabel: 'Categories', icon: Tag },
  { key: 'orders', label: 'Orders', shortLabel: 'Orders', icon: ShoppingBag },
  { key: 'stock', label: 'Stock Control', shortLabel: 'Stock', icon: Layers },
  { key: 'settings', label: 'Brand & Content', shortLabel: 'Settings', icon: Sliders },
]

const activeClass =
  'bg-gradient-to-r from-[var(--gold-main)] to-[var(--gold-dark)] text-[var(--gold-contrast)] font-semibold shadow-[0_0_18px_rgba(212,175,55,0.4)]'
const idleClass =
  'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]'

interface AdminSidebarProps {
  mobileOpen: boolean
  onMobileOpen: () => void
  onMobileClose: () => void
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  mobileOpen,
  onMobileOpen,
  onMobileClose,
}) => {
  const { user, logout } = useAuth()
  const { config } = useSiteConfig()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const activeTab = tabFromPath(pathname)

  const goTo = (key: AdminSubTab) => {
    navigate({ to: `/admin/${key}` })
    onMobileClose()
  }

  const NavLinks = ({ onItemClick }: { onItemClick?: () => void }) => (
    <>
      {ADMIN_TABS.map(({ key, label, icon: Icon }) => {
        const isActive = activeTab === key
        return (
          <button
            key={key}
            onClick={() => {
              goTo(key)
              onItemClick?.()
            }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all duration-200 min-h-[44px] ${
              isActive ? activeClass : idleClass
            }`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="truncate text-left">{label}</span>
          </button>
        )
      })}
    </>
  )

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <aside className="admin-print-hidden hidden md:flex fixed left-0 top-0 bottom-0 z-40 w-[240px] flex-col border-r border-[var(--border-gold)] bg-[var(--bg-surface)]/95 backdrop-blur-xl">
        <div className="px-4 py-5 flex items-center gap-2.5 border-b border-[var(--border)]">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-0.5">
            <div className="w-full h-full rounded-[10px] bg-[var(--bg-main)] flex items-center justify-center text-[var(--gold-main)] font-black">
              {config.brand_name ? config.brand_name.charAt(0) : 'B'}
            </div>
          </div>
          <div className="overflow-hidden min-w-0">
            <p className="text-sm font-bold text-[var(--text-primary)] leading-tight truncate">
              {config.brand_name}
            </p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--gold-main)]/80 truncate">
              Admin Suite
            </p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <NavLinks />
        </nav>

        <div className="px-3 py-4 border-t border-[var(--border)] space-y-3">
          <button
            onClick={() => navigate({ to: '/' })}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)] transition-all duration-200 min-h-[44px]"
          >
            <Store className="w-5 h-5 shrink-0" />
            <span>Back to Store</span>
          </button>

          <div className="flex items-center justify-between gap-2 px-3 pt-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 shrink-0 rounded-full bg-[var(--gold-soft-bg)] border border-[var(--gold-soft-border)] flex items-center justify-center text-xs font-bold text-[var(--gold-main)]">
                {(user?.name || user?.phone || 'A').charAt(0).toUpperCase()}
              </div>
              <span className="text-xs text-[var(--text-faint)] truncate">{user?.name || 'Admin'}</span>
            </div>
            <button
              onClick={() => {
                logout()
                navigate({ to: '/' })
              }}
              title="Logout"
              className="p-2 text-[var(--text-faint)] hover:text-red-400 transition hover:bg-[var(--bg-surface-2)] rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Powered by Yonraa Tech */}
          <a
            href="https://yonraa.pages.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="pt-2 border-t border-[var(--border)] flex items-center justify-center gap-1.5 text-[10px] text-[var(--text-faint)] hover:text-[var(--gold-main)] transition cursor-pointer"
            title="Visit Yonraa Tech"
          >
            <span>Powered by</span>
            <img src="/yonraa-tech-logo.png" alt="Yonraa Tech" className="h-4 w-auto object-contain" />
            <span className="font-bold text-[var(--text-muted)] hover:text-[var(--gold-main)]">Yonraa Tech</span>
          </a>
        </div>
      </aside>

      {/* ── Mobile slide-over drawer ── */}
      {mobileOpen && (
        <div className="admin-print-hidden md:hidden fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-[var(--bg-main)]/70 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-[min(100vw-3rem,300px)] bg-[var(--bg-surface)] border-r border-[var(--border-gold)] shadow-2xl flex flex-col animate-fade-in-up">
            <div className="px-4 py-4 flex items-center justify-between border-b border-[var(--border)]">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-0.5">
                  <div className="w-full h-full rounded-[10px] bg-[var(--bg-main)] flex items-center justify-center text-[var(--gold-main)] font-black">
                    {config.brand_name ? config.brand_name.charAt(0) : 'B'}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[var(--text-primary)] truncate">{config.brand_name}</p>
                  <p className="text-[10px] text-[var(--gold-main)]">Admin Menu</p>
                </div>
              </div>
              <button
                onClick={onMobileClose}
                className="p-2 rounded-lg text-[var(--text-muted)] hover:bg-[var(--bg-surface-2)] min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              <NavLinks onItemClick={onMobileClose} />
            </nav>

            <div className="px-3 py-4 border-t border-[var(--border)] space-y-2 pb-[env(safe-area-inset-bottom)]">
              <button
                onClick={() => {
                  navigate({ to: '/' })
                  onMobileClose()
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-[var(--gold-main)] bg-[var(--gold-soft-bg)] border border-[var(--gold-soft-border)] min-h-[44px]"
              >
                <Store className="w-5 h-5 shrink-0" />
                <span>Back to Storefront</span>
              </button>
              <button
                onClick={() => {
                  logout()
                  navigate({ to: '/login' })
                  onMobileClose()
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-red-400 hover:bg-red-500/10 min-h-[44px]"
              >
                <LogOut className="w-5 h-5 shrink-0" />
                <span>Logout</span>
              </button>

              <a
                href="https://yonraa.pages.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="pt-2 border-t border-[var(--border)] flex items-center justify-center gap-1.5 text-[10px] text-[var(--text-faint)] hover:text-[var(--gold-main)] transition cursor-pointer"
                title="Visit Yonraa Tech"
              >
                <span>Powered by</span>
                <img src="/yonraa-tech-logo.png" alt="Yonraa Tech" className="h-4 w-auto object-contain" />
                <span className="font-bold text-[var(--text-muted)] hover:text-[var(--gold-main)]">Yonraa Tech</span>
              </a>
            </div>
          </aside>
        </div>
      )}

      {/* ── Mobile bottom bar (4 items max) ── */}
      <nav className="admin-print-hidden md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--bg-surface)]/95 backdrop-blur-xl border-t border-[var(--border-gold)] safe-area-bottom">
        <div className="grid grid-cols-4 gap-0 px-1 pt-1">
          {MOBILE_BOTTOM_TABS.map((key) => {
            const tab = ADMIN_TABS.find((t) => t.key === key)!
            const Icon = tab.icon
            const isActive = activeTab === key
            return (
              <button
                key={key}
                onClick={() => goTo(key)}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg min-h-[56px] transition-colors ${
                  isActive
                    ? 'text-[var(--gold-main)] bg-[var(--gold-soft-bg)]'
                    : 'text-[var(--text-faint)]'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="text-[10px] font-semibold leading-tight">{tab.shortLabel}</span>
              </button>
            )
          })}
          <button
            onClick={onMobileOpen}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg min-h-[56px] transition-colors ${
              mobileOpen || !MOBILE_BOTTOM_TABS.includes(activeTab)
                ? 'text-[var(--gold-main)] bg-[var(--gold-soft-bg)]'
                : 'text-[var(--text-faint)]'
            }`}
            aria-label="More menu"
          >
            <MoreHorizontal className="w-5 h-5 shrink-0" />
            <span className="text-[10px] font-semibold leading-tight">More</span>
          </button>
        </div>
      </nav>
    </>
  )
}

/** Mobile header menu button — use in AdminLayout */
export const AdminMobileMenuButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="md:hidden p-2 -ml-1 rounded-lg text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)] min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
    aria-label="Open admin menu"
  >
    <Menu className="w-5 h-5" />
  </button>
)

export default AdminSidebar
